import { formatDate, CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';

import { CourseVisit } from 'src/app/models/course-visit';
import { Student } from 'src/app/models/student';
import { StudentVisit } from 'src/app/models/student-visit';
import { Visit } from 'src/app/models/visit';
import { StudentEditComponent } from '../students/student-edit/student-edit.component';

import { EnrollService } from 'src/app/services/enroll/enroll.service';
import { StudentsService } from 'src/app/services/students/students.service';
import { VisitsService } from 'src/app/services/visits/visits.service';
import { CheckinService } from 'src/app/services/checkin/checkin.service';
import { Room } from 'src/app/models/checkin';
import { combineLatest } from 'rxjs';

import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NamePipe } from 'src/app/pipes/name.pipe';
import { MatTableModule } from '@angular/material/table';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'li-visits',
  templateUrl: './visits.component.html',
  styleUrls: ['./visits.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    NamePipe,
    FormsModule,
    StudentEditComponent,
    MatIconModule,
    MatTooltipModule
  ]
})
export class VisitsComponent implements OnInit {

  @Input()
  public date!: string;

  @Input()
  public time!: string;

  public pastDate: string = '';
  public pastTime: string = '';
  public today!: string;
  public loading = true;

  public courseVisit?: CourseVisit;

  /** Enrolled students. */
  public dataSource = new MatTableDataSource<StudentVisit>();

  /** Trial visitors: visited without an enrollment ("Probestunde"). */
  public trialSource = new MatTableDataSource<StudentVisit>();

  /** Plans (rooms) for the selector; parallel courses are in different plans. */
  public rooms: Room[] = [];

  /** Selected plan, from the query parameter pid (default 1). */
  public pid = 1;

  /**
   * Bestehende Schüler, die noch nicht
   * dem aktuellen Kurs zugewiesen sind.
   */
  public availableStudents: Student[] = [];

  public studentSearch = "";

  public newStudent?: Student;

  /**
   * Steuert die Anzeige des Auswahl-Overlays.
   */
  public showStudentSelection = false;

  private courseDate: Date = new Date();

  private messageVisitChangeError: string = 'Die Anwesenheit konnte nicht geändert werden.';
  private messageNoCourseFound: string = 'Für dieses Datum/Zeit wurde kein Kurs gefunden.';

  constructor(
    private visitsService: VisitsService,
    private enrollService: EnrollService,
    private studentsService: StudentsService,
    private checkinService: CheckinService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  /** Shows the same date and time in another plan. */
  public selectPlan(pid: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { pid },
      queryParamsHandling: 'merge'
    });
  }

  public goToPastCourse(): void {
    if (!this.pastDate || !this.pastTime) return;

    const now = new Date();

    const selectedDay = new Date(this.pastDate + 'T00:00:00');
    const today = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );

    if (selectedDay > today) {
      alert('Datum darf nicht in der Zukunft liegen!');
      return;
    }

    const dateParam = this.pastDate.split('-').join('');
    const timeParam = this.pastTime.split(':').join('');

    this.router.navigate(
      ['/visits', dateParam, timeParam],
      { queryParams: { pid: this.pid } }
    );
  }

  ngOnInit(): void {
    this.setDefaultPastDateTime();

    this.checkinService.rooms().subscribe({
      next: rooms => this.rooms = rooms,
      error: () => this.rooms = []
    });

    combineLatest([this.route.params, this.route.queryParams]).subscribe(([params, query]) => {
      this.date = params['date'];
      this.time = params['time'];
      this.pid = Number(query['pid']) || 1;

      if (this.date && this.time) {
        this.prepareCourseDate();
        this.getData();
      } else {
        this.loading = false;
        this.courseVisit = undefined;
        this.dataSource.data = [];
        this.trialSource.data = [];
      }
    });
  }

  private setDefaultPastDateTime(): void {
    const now = new Date();

    this.today = now.toISOString().slice(0, 10);
    this.pastDate = this.today;

    const hour = now.getHours().toString().padStart(2, '0');
    this.pastTime = `${hour}:00`;
  }

  private prepareCourseDate(): void {
    if (this.date.length === 8 && this.time.length >= 3) {
      const fulltime = this.time.padStart(4, '0');

      const datetime =
        `${this.date.substring(0, 4)}-` +
        `${this.date.substring(4, 6)}-` +
        `${this.date.substring(6, 8)} ` +
        `${fulltime.substring(0, 2)}:` +
        `${fulltime.substring(2, 4)}:00`;

      const selectedDate = new Date(datetime);
      const now = new Date();

      this.courseDate = selectedDate;
    }
  }

  private getData(): void {
    this.loading = true;

    this.courseVisit = undefined;
    this.dataSource.data = [];
    this.trialSource.data = [];

    this.visitsService.get(this.courseDate, this.pid).subscribe({
      next: courseVisit => {
        this.loading = false;

        if (!courseVisit) {
          alert(this.messageNoCourseFound);
          return;
        }

        this.courseVisit = courseVisit;
        this.dataSource.data = courseVisit.students.filter(s => !isTrial(s));
        this.trialSource.data = courseVisit.students.filter(s => isTrial(s));
      },

      error: err => {
        console.error('Error loading course visit:', err);

        this.loading = false;
        this.courseVisit = undefined;
        this.dataSource.data = [];

        alert(this.messageNoCourseFound);
      }
    });
  }

  public visit(studentVisit: StudentVisit): void {
    if (!this.courseVisit) return;

    const visit: Visit = {
      cid: this.courseVisit.cid,
      sid: studentVisit.sid,
      date: this.courseVisit.date
    };

    if (isTrial(studentVisit)) {
      if (!confirm(`Probestunde von ${studentVisit.firstname} ${studentVisit.lastname} entfernen?`)) {
        this.trialSource.data = [...this.trialSource.data]; // re-check the box
        return;
      }
      this.visitsService.del(visit).subscribe({
        next: () => this.getData(),
        error: _ => { alert(this.messageVisitChangeError); }
      });
      return;
    }

    if (studentVisit.visited > 0) {
      this.visitsService.del(visit).subscribe({
        next: result => { this.handleResultSuccess(studentVisit, result, 0) },
        error: _ => { alert(this.messageVisitChangeError); }
      });
    } else {
      this.visitsService.set(visit).subscribe({
        next: result => { this.handleResultSuccess(studentVisit, result, 1) },
        error: _ => { alert(this.messageVisitChangeError); }
      });
    }
  }

  /** Enrolls a trial visitor from their first trial visit in this course. */
  public enrollTrial(studentVisit: StudentVisit): void {
    if (!this.courseVisit) return;

    const begin = studentVisit.first ?? formatDate(this.courseDate, 'yyyy-MM-dd', 'en-US');
    this.enrollService.add(studentVisit.sid, this.courseVisit.cid, begin, '2100-01-01').subscribe({
      next: () => this.getData(),
      error: () => alert(`${studentVisit.firstname} ${studentVisit.lastname} konnte nicht eingetragen werden.`)
    });
  }

  public remove(studentVisit: StudentVisit): void {
    if (!this.courseVisit) {
      return;
    }

    if (!confirm(`Soll ${studentVisit.firstname} ${studentVisit.lastname} wirklich aus diesem Kurs entfernt werden?`)) {
      return;
    }

    this.enrollService
      .del(this.courseVisit.cid, studentVisit.sid, this.courseDate)
      .subscribe(() => this.getData());
  }

  public get filteredStudents(): Student[] {
    const search = this.studentSearch.trim().toLowerCase();

    if (!search) {
      return this.availableStudents;
    }

    return this.availableStudents.filter(student => {
      const firstname = student.firstname?.toLowerCase() ?? '';
      const lastname = student.lastname?.toLowerCase() ?? '';

      return (
        firstname.includes(search) ||
        lastname.includes(search) ||
        `${firstname} ${lastname}`.includes(search)
      );
    });
  }

  public addNewStudent(): void {
    const search = this.studentSearch.trim();

    if (!search) return;

    const parts = search.split(/\s+/);

    this.newStudent = <Student>{
      firstname: parts.shift() || '',
      lastname: parts.join(' ') || ''
    };

    this.showStudentSelection = false;
  }

  /**
   * A student created on demand is a trial visitor: only the visit is
   * stored, no enrollment.
   */
  public newStudentSaved(student?: Student): void {
    this.newStudent = undefined;

    // Closed without saving
    if (!student) return;

    if (!this.courseVisit || !student.sid) {
      this.getData();
      return;
    }

    this.storeVisit(student.sid, 'Der neue Schüler wurde angelegt, die Probestunde konnte aber nicht eingetragen werden.');
    this.studentSearch = '';
  }

  /**
   * Öffnet die Auswahl der bereits existierenden Schüler.
   */
  public add(): void {
    if (!this.courseVisit) return;

    this.studentSearch = '';

    this.studentsService.get().subscribe({
      next: students => {

        const enrolledIds = this.courseVisit!.students.map(
          student => student.sid
        );

        this.availableStudents = students.filter(
          student => !enrolledIds.includes(student.sid)
        );

        this.showStudentSelection = true;
      },

      error: err => {
        console.error('Error loading students:', err);

        this.availableStudents = [];

        alert('Die Schüler konnten nicht geladen werden.');
      }
    });
  }

  /**
   * Schließt die Schülerauswahl.
   */
  public closeStudentSelection(): void {
    this.showStudentSelection = false;
    this.availableStudents = [];
    this.studentSearch = '';
  }

  /** Existing student, "Probestunde": only the visit. */
  public addTrial(student: Student): void {
    this.closeStudentSelection();
    this.storeVisit(student.sid, 'Die Probestunde konnte nicht eingetragen werden.');
  }

  /** Existing student, "Zum Kurs hinzufügen": enrollment from this date plus the visit. */
  public addToCourse(student: Student): void {
    if (!this.courseVisit) return;

    const begin = formatDate(this.courseDate, 'yyyy-MM-dd', 'en-US');

    this.enrollService.add(student.sid, this.courseVisit.cid, begin, '2100-01-01').subscribe({
      next: () => {
        this.closeStudentSelection();
        this.storeVisit(student.sid, 'Der Teilnehmer wurde eingetragen, die Anwesenheit konnte aber nicht gespeichert werden.');
      },
      error: err => {
        console.error('Error enrolling student:', err);
        alert('Der Teilnehmer konnte nicht hinzugefügt werden.');
      }
    });
  }

  private storeVisit(sid: number, errorMessage: string): void {
    if (!this.courseVisit) return;

    const visit: Visit = { cid: this.courseVisit.cid, sid, date: this.courseVisit.date };
    this.visitsService.set(visit).subscribe({
      next: () => this.getData(),
      error: err => {
        console.error('Error storing visit:', err);
        alert(errorMessage);
        this.getData();
      }
    });
  }

  private handleResultSuccess(studentVisit: StudentVisit, result: boolean, value: number): void {
    if (result) {
      studentVisit.visited = value;
    } else {
      alert(this.messageVisitChangeError);
    }
  }
}

function isTrial(student: StudentVisit): boolean {
  return Number(student.trial ?? 0) > 0;
}
